import React, { useCallback, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import TopicScreen from './topic-screen';
import ChatScreen from './chat-screen';
import { sendMessage, STARTER_MESSAGES } from './anthropic';
import { useUserAppsContext } from '../user-apps-context';

export default function Buddy() {
    const { t } = useTranslation();
    const { userProfile } = useUserAppsContext();
    const [topic, setTopic] = useState(null);
    const [messages, setMessages] = useState([]);         // {role, content} for display
    const [apiMessages, setApiMessages] = useState([]);   // {role, content} for API
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);

    const handleSelectTopic = useCallback(async (selectedTopic) => {
        setTopic(selectedTopic);
        setLoading(true);

        const starterText = STARTER_MESSAGES[selectedTopic];
        const firstMessage = [{ role: 'user', content: starterText }];

        setMessages([{ role: 'user', content: starterText }]);
        setApiMessages(firstMessage);

        try {
            const response = await sendMessage(firstMessage, selectedTopic, userProfile.context);
            setMessages(prev => [...prev, { role: 'assistant', content: response.message }]);
            setApiMessages(prev => [...prev, { role: 'assistant', content: response.message }]);
            setOptions(response.options);
        } catch (error) {
            console.error('Buddy: API error', error);
            setMessages(prev => [...prev, { role: 'assistant', content: t('APPLICATIONS.BUDDY.ERROR') }]);
            setOptions([t('APPLICATIONS.BUDDY.RETRY')]);
        } finally {
            setLoading(false);
        }
    }, [t, userProfile.context]);

    const handleSelectOption = useCallback(async (selectedOption) => {
        if (selectedOption === t('APPLICATIONS.BUDDY.RETRY')) {
            // On retry, resend the last user message
            setMessages(prev => prev.slice(0, -1)); // remove error message
            setLoading(true);
            try {
                const response = await sendMessage(apiMessages, topic, userProfile.context);
                setMessages(prev => [...prev, { role: 'assistant', content: response.message }]);
                setApiMessages(prev => [...prev, { role: 'assistant', content: response.message }]);
                setOptions(response.options);
            } catch (error) {
                console.error('Buddy: API error', error);
                setMessages(prev => [...prev, { role: 'assistant', content: t('APPLICATIONS.BUDDY.ERROR') }]);
                setOptions([t('APPLICATIONS.BUDDY.RETRY')]);
            } finally {
                setLoading(false);
            }
            return;
        }

        setLoading(true);
        setOptions([]);

        const newUserMsg = { role: 'user', content: selectedOption };
        setMessages(prev => [...prev, newUserMsg]);

        const newApiMessages = [...apiMessages, newUserMsg];
        setApiMessages(newApiMessages);

        try {
            const response = await sendMessage(newApiMessages, topic, userProfile.context);
            setMessages(prev => [...prev, { role: 'assistant', content: response.message }]);
            setApiMessages(prev => [...prev, { role: 'assistant', content: response.message }]);
            setOptions(response.options);
        } catch (error) {
            console.error('Buddy: API error', error);
            setMessages(prev => [...prev, { role: 'assistant', content: t('APPLICATIONS.BUDDY.ERROR') }]);
            setOptions([t('APPLICATIONS.BUDDY.RETRY')]);
        } finally {
            setLoading(false);
        }
    }, [apiMessages, topic, t, userProfile.context]);

    const handleBack = useCallback(() => {
        setTopic(null);
        setMessages([]);
        setApiMessages([]);
        setOptions([]);
        setLoading(false);
    }, []);

    // Topic selection screen
    if (!topic) {
        return <TopicScreen onSelectTopic={handleSelectTopic} />;
    }

    // Initial loading after topic selection
    if (loading && messages.length <= 1) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
                <CircularProgress size={48} />
                <Typography variant="h6" color="text.secondary">
                    {t('APPLICATIONS.BUDDY.LOADING')}
                </Typography>
            </Box>
        );
    }

    return (
        <ChatScreen
            messages={messages}
            options={options}
            loading={loading}
            onSelectOption={handleSelectOption}
            onBack={handleBack}
        />
    );
}
